/**
 * Action Routing Section — standalone settings section.
 *
 * Lets an org admin choose which users receive proposals from ownerless
 * background triggers. The backing settings endpoint is admin-gated (both GET
 * and PUT require the admin group), so the pool contents are not fetched for
 * non-admins — they see a read-only notice rather than a misleading empty
 * picker.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import UserAssignmentPicker from '@/components/crm/UserAssignmentPicker';
import { useOrgMembers } from '@/hooks/useOrgMembers';
import { usePermissions } from '@/hooks/usePermissions';
import { useActionRoutingPool } from '@/lib/action-routing/useActionRoutingPool';
import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { logger } from '@/lib/logger';
import { useViewHealthMarker } from '@/lib/view-health';

import { useOrg } from '../../../../contexts/OrgContext';
import { ACTION_ROUTING_ADMIN_PERMISSION } from '../settings-permissions';

function sameUserIdSet(a: number[], b: number[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const bSet = new Set(b);
  return a.every(id => bSet.has(id));
}

function RoutingPoolForm({ canEdit }: { canEdit: boolean }) {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id ?? null;

  const {
    userIds,
    isLoading: poolLoading,
    isError: poolError,
    save,
    isSaving,
  } = useActionRoutingPool(orgId ?? undefined, { enabled: canEdit });
  const {
    members,
    isLoading: membersLoading,
    isError: membersError,
  } = useOrgMembers(orgId);

  const availableUsers = useMemo(
    () =>
      members
        .filter(m => m.deactivatedAt == null && m.memberKind !== 'viewer')
        .map(m => ({
          id: m.userId,
          email: m.email,
          display: m.displayName || `${m.firstName} ${m.lastName}`.trim(),
        })),
    [members]
  );

  const [selectedUserIds, setSelectedUserIds] = useState<number[]>(userIds);
  useEffect(() => {
    setSelectedUserIds(userIds);
  }, [userIds]);

  const [error, setError] = useState<string | null>(null);

  const hasChanges = !sameUserIdSet(selectedUserIds, userIds);

  const handleSave = useCallback(async () => {
    setError(null);
    try {
      await save(selectedUserIds);
      toast.success('Action routing saved');
    } catch (err) {
      logger.error(
        'Failed to save action routing',
        err instanceof Error ? err : new Error(String(err)),
        { org_id: orgId }
      );
      setError('We couldn’t save your changes. Try again in a moment.');
      toast.error('We couldn’t save your changes');
    }
  }, [save, selectedUserIds, orgId]);

  useEffect(() => {
    if (!canEdit) {
      logger.debug('Action routing pool fetch skipped (non-admin)', {
        org_id: orgId,
      });
    }
  }, [canEdit, orgId]);

  // Non-admin: the settings endpoint is admin-gated on the backend (GET requires
  // the admin group), so the pool query is intentionally skipped and its contents
  // cannot be shown here. Render a read-only notice only — never a picker seeded
  // with empty data, which would misleadingly imply the pool is empty.
  if (!canEdit) {
    return (
      <p className='text-sm text-fg-tertiary'>
        Only workspace admins can view or change action routing. Ask an admin if
        you need access.
      </p>
    );
  }

  // Admin path: pool query runs; gate on loading and error.
  if (poolLoading || membersLoading) {
    return <LoadingIndicator />;
  }

  if (poolError || membersError) {
    return (
      <p className='text-sm text-error-primary'>
        We couldn&apos;t load your routing settings. Refresh the page to try
        again.
      </p>
    );
  }

  return (
    <div>
      <div className='space-y-4'>
        <UserAssignmentPicker
          selectedUserIds={selectedUserIds}
          onChange={setSelectedUserIds}
          availableUsers={availableUsers}
          isDisabled={false}
        />

        <p className='text-sm text-fg-tertiary'>
          Each action goes to one person on this list, not to everyone. We start
          the list with your workspace admins.
        </p>

        {error && <p className='text-sm text-error-primary'>{error}</p>}
      </div>

      <div className='mt-4 flex justify-end'>
        <Button
          color='primary'
          size='md'
          onClick={handleSave}
          isDisabled={!hasChanges || isSaving}
          isLoading={isSaving}
        >
          Save changes
        </Button>
      </div>
    </div>
  );
}

export function ActionRoutingSection() {
  const { hasPermissionGroup } = usePermissions();
  const canEdit = hasPermissionGroup(ACTION_ROUTING_ADMIN_PERMISSION);
  useViewHealthMarker('settings-section-ready');

  return (
    <div className='space-y-8'>
      <div>
        <h2 className='text-lg font-semibold text-fg-primary'>
          Action routing
        </h2>
        <p className='text-sm text-fg-tertiary mt-0.5'>
          Not every action has an owner – like an inbound form submission, or an
          unowned record going stale. Choose who picks these up when no one else
          does.
        </p>
      </div>
      <div>
        <RoutingPoolForm canEdit={canEdit} />
      </div>
    </div>
  );
}
